import { appSessionStorage } from '@utils/storageUtils'
import { message } from 'antd'
import { useEffect, useState } from 'react'

import { Entities_Modules_SlugLink } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'

interface UseSlugLinksReturn {
  slugLinks: Entities_Modules_SlugLink[]
  isLoading: boolean
  error: Error | null
  refetchSlugLinks: () => Promise<void>
  getGameUrl: (criteria: string[]) => string
}

/**
 * Custom hook for managing slug links
 * @returns {UseSlugLinksReturn} Object containing slugLinks, loading state, error, and utility functions
 */
export const useSlugLinks = (): UseSlugLinksReturn => {
  const { api } = useFramework()
  const [slugLinks, setSlugLinks] = useState<Entities_Modules_SlugLink[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetches slug links from the API
   */
  const fetchSlugLinks = async (): Promise<void> => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await api.get_Modules_SlugLinks({})
      if (response.items) {
        setSlugLinks(response.items)
        appSessionStorage.slugLinks = response.items
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to fetch slug links')
      setError(error)
      message.error('Failed to fetch slug links')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Refetches slug links, bypassing session storage
   */
  const refetchSlugLinks = async (): Promise<void> => {
    await fetchSlugLinks()
  }

  // Load slug links on mount
  useEffect(() => {
    if (appSessionStorage.slugLinks.length === 0) {
      void fetchSlugLinks()
    } else {
      setSlugLinks(appSessionStorage.slugLinks)
    }
  }, [])

  const getGameUrl = (criteria: string[]): string => {
    if (slugLinks.length === 0) return ''
    const matchingItem =
      slugLinks.find((item) =>
        criteria.every((term) =>
          item.text?.toLowerCase().includes(term.toLowerCase())
        )
      ) || null
    let url = ''
    if (matchingItem?.urlClassic.url) {
      url = `${matchingItem.urlClassic.url}`
    }
    return url
  }

  return {
    slugLinks,
    isLoading,
    error,
    refetchSlugLinks,
    getGameUrl
  }
}
